import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Linkedin, Twitter, ArrowRight, Heart, Users, Award, BookOpen, ChevronLeft, ChevronRight } from 'lucide-react';
import { teamMembers, mentors, testimonials, sponsors } from '../mock';

// Component for images with fallback on error
const FallbackImage = ({ src, alt, className, fallbackClassName }) => {
  const [hasError, setHasError] = useState(false);
  
  if (hasError) {
    return (
      <div className={`${fallbackClassName || className} bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center`}>
        <span className="text-white text-2xl font-bold">{alt?.charAt(0) || '?'}</span>
      </div>
    );
  }
  
  return (
    <img 
      src={src} 
      alt={alt}
      className={className}
      onError={() => setHasError(true)}
    />
  );
};

const Home = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  // Auto-play carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(timer);
  }, []);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const goToTestimonial = (index) => {
    setCurrentTestimonial(index);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center justify-center text-white overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&h=1080&fit=crop)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/90 via-blue-600/85 to-purple-600/80"></div>
        
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white/5 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-300/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
        
        <div className="relative z-10 text-center px-4 max-w-5xl mx-auto py-20">
          <h2 className="text-xl md:text-2xl font-light mb-6 tracking-wide min-h-[2em] flex flex-wrap justify-center items-center gap-3">
            <span className="inline-block" style={{ 
              opacity: 0, 
              animation: 'fade-in 0.5s ease-in forwards',
              animationDelay: '0.3s'
            }}>Student?</span>
            
            <span className="inline-block" style={{ 
              opacity: 0, 
              animation: 'fade-in 0.5s ease-in forwards',
              animationDelay: '0.8s'
            }}>Immigrant?</span>
            
            <span className="inline-block" style={{ 
              opacity: 0, 
              animation: 'fade-in 0.5s ease-in forwards',
              animationDelay: '1.3s'
            }}>Minority?</span>
            
            <span className="inline-block" style={{ 
              opacity: 0, 
              animation: 'fade-in 0.5s ease-in forwards',
              animationDelay: '1.8s'
            }}>Ready for Success?</span>
          </h2>
          
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-extrabold mb-8 leading-tight" style={{ opacity: 0, animation: 'fade-in-up 1s ease-out 2.5s forwards' }}>
            <span className="block mb-2" style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontWeight: '400' }}>Sign Up</span>
            <span className="block font-black">to Become a <span style={{ color: '#D4AF37' }}>Cub!</span></span>
          </h1>
          
          <p className="text-lg md:text-xl mb-10 text-white/90 max-w-2xl mx-auto" style={{ opacity: 0, animation: 'fade-in 0.8s ease-out 3.2s forwards' }}>
            Join our mentorship program and unlock opportunities that will transform your career
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center" style={{ opacity: 0, animation: 'fade-in-up 0.8s ease-out 3.8s forwards' }}>
            <Link to="/register/cub">
              <Button 
                size="lg" 
                className="text-lg px-10 py-7 font-bold hover:scale-105 hover:shadow-2xl transition-all rounded-full group border-2"
                style={{ 
                  background: 'linear-gradient(135deg, #D4AF37 0%, #F4D03F 100%)',
                  color: '#1a1a1a',
                  borderColor: '#D4AF37'
                }}
              >
                SIGN UP NOW
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/mission">
              <Button 
                size="lg" 
                variant="outline"
                className="text-lg px-10 py-7 font-bold hover:scale-105 transition-all border-2 border-white text-white hover:bg-white hover:text-cyan-600 rounded-full"
              >
                LEARN MORE
              </Button>
            </Link>
          </div>
          
          {/* Stats */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-8 max-w-3xl mx-auto px-4" style={{ opacity: 0, animation: 'fade-in-up 0.8s ease-out 4.4s forwards' }}>
            <div className="backdrop-blur-sm bg-white/10 rounded-2xl p-3 md:p-4 border border-white/20 flex flex-col items-center justify-center min-h-[100px]">
              <div className="text-sm sm:text-2xl md:text-3xl font-bold mb-1">500+</div>
              <div className="text-xs sm:text-sm text-white/80 text-center">Cubs Mentored</div>
            </div>
            <div className="backdrop-blur-sm bg-white/10 rounded-2xl p-3 md:p-4 border border-white/20 flex flex-col items-center justify-center min-h-[100px]">
              <div className="text-sm sm:text-2xl md:text-3xl font-bold mb-1">50+</div>
              <div className="text-xs sm:text-sm text-white/80 text-center">Expert Lions</div>
            </div>
            <div className="backdrop-blur-sm bg-white/10 rounded-2xl p-3 md:p-4 border border-white/20 flex flex-col items-center justify-center min-h-[100px]">
              <div className="text-sm sm:text-2xl md:text-3xl font-bold mb-1">100+</div>
              <div className="text-xs sm:text-sm text-white/80 text-center">Workshops</div>
            </div>
            <div className="backdrop-blur-sm bg-white/10 rounded-2xl p-3 md:p-4 border border-white/20 flex flex-col items-center justify-center min-h-[100px]">
              <div className="text-sm sm:text-2xl md:text-3xl font-bold mb-1">$100K+</div>
              <div className="text-xs sm:text-sm text-white/80 text-center">Scholarships</div>
            </div>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-0 right-0 flex justify-center animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-white rounded-full"></div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 bg-white relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-cyan-100/30 to-blue-100/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-purple-100/30 to-cyan-100/30 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <span className="inline-block px-4 py-2 bg-gradient-to-r from-cyan-100 to-blue-100 rounded-full text-cyan-700 font-semibold text-sm mb-4">
                About Us
              </span>
              <h2 className="text-3xl md:text-5xl lg:text-6xl font-extrabold mb-6 leading-tight">
                <span style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontWeight: '400' }}>Empowering</span>{' '}
                <span className="font-black">
                  <span style={{ color: '#D4AF37' }}>Future</span> Leaders
                </span>
              </h2>
              <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
                Since 2019, we've been bridging the gap between ambition and achievement
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-16 items-center mb-16">
              <div className="space-y-6 text-base md:text-lg text-gray-700 leading-relaxed">
                <p className="text-lg md:text-xl">
                  <strong className="text-gray-900 text-xl md:text-2xl">Lions Unleashed</strong> is a nonprofit organization striving to provide extensive resources, mentorship, and networking opportunities to minorities and young entrepreneurs.
                </p>
                <p>
                  In today's society, young immigrants and minorities often face challenges in achieving their dreams due to limited networks and resources. That's where we step in!
                </p>
                <p>
                  Our team of accomplished mentors—known as <strong className="text-cyan-600">Lions</strong>—are successful entrepreneurs and corporate leaders ready to guide <strong className="text-blue-600">Cubs</strong> through their career journeys.
                </p>
                <p className="mb-6">
                  Cubs who apply to this program will also have access to a variety of free resources ranging from workshops, job opportunities, and scholarships.
                </p>
                
                <div className="bg-gradient-to-r from-yellow-50 to-amber-50 rounded-2xl p-6 md:p-8 border-l-4" style={{ borderColor: '#D4AF37' }}>
                  <p className="font-semibold text-lg md:text-xl leading-relaxed" style={{ color: '#B8860B' }}>
                    ✨ As a Cub, take advantage of this FREE opportunity and join our pride today!
                  </p>
                </div>
                
                <Link to="/mission" className="inline-block mt-8">
                  <Button size="lg" className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white hover:shadow-xl transition-all rounded-full group px-8 py-6 text-base md:text-lg">
                    Discover Our Mission
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </div>
              
              <div className="grid grid-cols-2 gap-6">
                <Card className="group hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-0 bg-gradient-to-br from-cyan-50 to-white">
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Users className="w-8 h-8 text-white" />
                    </div>
                    <h4 className="text-4xl font-extrabold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent mb-2">500+</h4>
                    <p className="text-gray-700 font-medium">Cubs Mentored</p>
                  </CardContent>
                </Card>
                
                <Card className="group hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-0 bg-gradient-to-br from-blue-50 to-white">
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Award className="w-8 h-8 text-white" />
                    </div>
                    <h4 className="text-4xl font-extrabold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">50+</h4>
                    <p className="text-gray-700 font-medium">Expert Lions</p>
                  </CardContent>
                </Card>
                
                <Card className="group hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-0 bg-gradient-to-br from-purple-50 to-white">
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-purple-500 to-cyan-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                      <BookOpen className="w-8 h-8 text-white" />
                    </div>
                    <h4 className="text-4xl font-extrabold bg-gradient-to-r from-purple-600 to-cyan-600 bg-clip-text text-transparent mb-2">100+</h4>
                    <p className="text-gray-700 font-medium">Workshops</p>
                  </CardContent>
                </Card>
                
                <Card className="group hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-0 bg-gradient-to-br from-cyan-50 to-purple-50">
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-cyan-500 to-purple-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Heart className="w-8 h-8 text-white" />
                    </div>
                    <h4 className="text-4xl font-extrabold bg-gradient-to-r from-cyan-600 to-purple-600 bg-clip-text text-transparent mb-2">$100K+</h4>
                    <p className="text-gray-700 font-medium">Scholarships</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-white rounded-full text-cyan-700 font-semibold text-sm mb-4 shadow-sm">
              Our Team
            </span>
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-extrabold mb-4 leading-tight">
              <span style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontWeight: '400' }}>Meet Our</span>{' '}
              <span className="font-black">
                <span style={{ color: '#D4AF37' }}>Leaders</span>
              </span>
            </h2>
            <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-2xl mx-auto">
              Dedicated professionals committed to your success
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 max-w-7xl mx-auto">
            {teamMembers.map((member, index) => (
              <div key={member.id} className="group" style={{ animationDelay: `${index * 100}ms` }}>
                <div className="bg-white rounded-2xl p-6 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-gray-100">
                  <div className="relative mb-4 overflow-hidden rounded-full mx-auto">
                    <FallbackImage 
                      src={member.image} 
                      alt={member.name}
                      className="w-full aspect-square object-cover group-hover:scale-110 transition-transform duration-500 rounded-full"
                      fallbackClassName="w-full aspect-square rounded-full"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-full"></div>
                  </div>
                  <h4 className="text-sm md:text-base font-bold text-gray-900 mb-1 text-center">{member.name}</h4>
                  <p className="text-xs md:text-sm text-gray-600 mb-3 text-center">{member.role}</p>
                  <div className="flex justify-center space-x-2">
                    {member.linkedin && (
                      <a 
                        href={member.linkedin} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:scale-110 transition-transform"
                      >
                        <Linkedin size={16} />
                      </a>
                    )}
                    {member.twitter && (
                      <a 
                        href={member.twitter} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-r from-blue-500 to-purple-500 text-white hover:scale-110 transition-transform"
                      >
                        <Twitter size={16} />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder Message */}
      <section className="py-20 bg-gradient-to-r from-cyan-400 to-blue-500 text-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center mb-12 leading-tight">
            <span style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontWeight: '400' }}>A Message From Our</span>{' '}
            <span className="font-black">Founder</span>
          </h2>
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8 items-center">
              <div className="md:col-span-1">
                <img 
                  src="https://customer-assets.emergentagent.com/job_pride-support/artifacts/2gm0gk78_Facetune_20-05-2025-14-41-56.jpeg" 
                  alt="Wesley Ramjeet"
                  className="rounded-lg shadow-2xl w-full object-cover"
                />
                <div className="text-center mt-4">
                  <h3 className="text-lg md:text-xl font-bold">Wesley Ramjeet</h3>
                  <p className="text-xs md:text-sm opacity-90">Founder</p>
                </div>
              </div>
              <div className="md:col-span-2 text-base md:text-lg leading-relaxed">
                <p className="mb-4">
                  In 2019, I started Lions Unleashed with my daughter and friends to focus on immigrant and minority youth. Our mission is quite simple; identify young and talented immigrants and minorities and provide mentorship, networks, and resources to maximize their potential.
                </p>
                <p className="mb-4">
                  I started this organization because of my own experience as an immigrant from Guyana. I was lucky that my mom was college educated and I was able to navigate with help from her and my other family members. Many of my friends were not so fortunate.
                </p>
                <p>
                  Through Lions Unleashed, I aim to provide this new generation of hardworking individuals with numerous opportunities to grow and make their respective marks on the world.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Success Stories Carousel */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold text-center mb-4 leading-tight">
            <span style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontWeight: '400' }}>Success</span>{' '}
            <span className="font-black" style={{ color: '#D4AF37' }}>Stories</span>
          </h2>
          <p className="text-center text-gray-600 mb-12 text-base md:text-lg">Here are some success stories from the past!</p>
          
          {/* Carousel Container */}
          <div className="max-w-3xl mx-auto relative">
            {/* Carousel Content */}
            <div className="relative overflow-hidden rounded-2xl">
              <div 
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentTestimonial * 100}%)` }}
              >
                {testimonials.map((testimonial) => (
                  <div key={testimonial.id} className="w-full flex-shrink-0 px-2">
                    <Card className="shadow-xl border border-cyan-100">
                      <CardContent className="p-6 md:p-8">
                        <div className="flex flex-col md:flex-row items-center md:items-start gap-4 mb-4">
                          <img 
                            src={testimonial.image} 
                            alt={testimonial.name}
                            className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover border-2 border-cyan-200 shadow-md flex-shrink-0"
                          />
                          <div className="text-center md:text-left">
                            <h4 className="text-xl md:text-2xl font-bold text-gray-900 mb-1">{testimonial.name}</h4>
                            <p className="text-sm md:text-base text-cyan-600 font-semibold">{testimonial.role}</p>
                          </div>
                        </div>
                        <p className="text-sm md:text-base text-gray-700 leading-relaxed italic">"{testimonial.quote}"</p>
                      </CardContent>
                    </Card>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation Buttons */}
            <button
              onClick={prevTestimonial}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-8 w-10 h-10 md:w-12 md:h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-cyan-600 hover:bg-cyan-600 hover:text-white transition-all hover:scale-110 border-2 border-cyan-200"
              aria-label="Previous testimonial"
            >
              <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
            </button>
            <button
              onClick={nextTestimonial}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-8 w-10 h-10 md:w-12 md:h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-cyan-600 hover:bg-cyan-600 hover:text-white transition-all hover:scale-110 border-2 border-cyan-200"
              aria-label="Next testimonial"
            >
              <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
            </button>

            {/* Dots Indicator */}
            <div className="flex justify-center gap-3 mt-8">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    index === currentTestimonial 
                      ? 'bg-cyan-600 w-8' 
                      : 'bg-gray-300 hover:bg-cyan-400'
                  }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mentors Section */}
      <section className="py-20 bg-gradient-to-r from-cyan-50 to-blue-50 overflow-hidden">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold text-center mb-12 leading-tight">
            <span style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontWeight: '400' }}>Meet Our</span>{' '}
            <span className="font-black" style={{ color: '#D4AF37' }}>Mentors</span>
          </h2>
          
          {/* Scrollable Carousel Container */}
          <div className="relative -mx-4 px-4">
            <div 
              className="flex gap-6 md:gap-12 py-4 overflow-x-auto scrollbar-hide snap-x snap-mandatory"
              style={{ 
                scrollbarWidth: 'none', 
                msOverflowStyle: 'none',
                WebkitOverflowScrolling: 'touch'
              }}
            >
              {mentors.map((mentor) => {
                const MentorWrapper = mentor.linkedin ? 'a' : 'div';
                const wrapperProps = mentor.linkedin ? {
                  href: mentor.linkedin,
                  target: "_blank",
                  rel: "noopener noreferrer"
                } : {};
                
                return (
                  <MentorWrapper 
                    key={mentor.id} 
                    className="text-center group flex-shrink-0 snap-start pl-2 first:pl-0 block"
                    {...wrapperProps}
                  >
                    <div className="relative mb-4 overflow-hidden rounded-full mx-auto w-32 h-32 md:w-44 md:h-44 shadow-lg border-4 border-white group-hover:border-cyan-400 transition-colors">
                      <FallbackImage 
                        src={mentor.image} 
                        alt={mentor.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        fallbackClassName="w-full h-full rounded-full"
                      />
                    </div>
                    <div className="mt-3">
                      <p className="text-xs uppercase tracking-wide text-cyan-600 font-semibold mb-1">Mentor</p>
                      <h4 className={`font-bold text-sm md:text-base text-gray-900 whitespace-nowrap ${mentor.linkedin ? 'group-hover:text-cyan-600 transition-colors' : ''}`}>{mentor.name}</h4>
                    </div>
                  </MentorWrapper>
                );
              })}
              {/* Add padding at the end for last item visibility */}
              <div className="flex-shrink-0 w-4"></div>
            </div>
            {/* Scroll hint for mobile */}
            <p className="text-center text-gray-500 text-sm mt-4 md:hidden">← Swipe to see more →</p>
          </div>
        </div>
      </section>

      {/* Sponsors Section */}
      <section className="py-20 bg-white overflow-hidden">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold text-center mb-12 leading-tight">
            <span style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontWeight: '400' }}>Our Generous</span>{' '}
            <span className="font-black" style={{ color: '#D4AF37' }}>Sponsors</span>
          </h2>
          
          {/* Scrollable Container */}
          <div className="relative">
            <div 
              className="flex gap-8 md:gap-12 py-4 overflow-x-auto scrollbar-hide justify-start md:justify-center"
              style={{ 
                scrollbarWidth: 'none', 
                msOverflowStyle: 'none',
                WebkitOverflowScrolling: 'touch'
              }}
            >
              {sponsors.map((sponsor) => (
                <a 
                  key={sponsor.id} 
                  href={sponsor.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-shrink-0 grayscale hover:grayscale-0 transition-all duration-300 hover:scale-110"
                >
                  <img 
                    src={sponsor.logo} 
                    alt={sponsor.name}
                    className="h-20 object-contain"
                  />
                </a>
              ))}
            </div>
            {/* Scroll hint for mobile */}
            <p className="text-center text-gray-500 text-sm mt-4 md:hidden">← Swipe to see more →</p>
          </div>
        </div>
      </section>

      {/* Affiliation Section - World Family Organization */}
      <section className="py-20 bg-gradient-to-br from-cyan-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-2 bg-gradient-to-r from-cyan-100 to-blue-100 rounded-full text-cyan-700 font-semibold text-sm mb-4">
                Member Organization
              </span>
              <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-4 leading-tight">
                <span style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontWeight: '400' }}>Proud Member of the</span>{' '}
                <span className="font-black" style={{ color: '#D4AF37' }}>World Family Organization</span>
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-12 items-center bg-white rounded-3xl shadow-xl p-8 md:p-12">
              <div className="flex justify-center">
                <a href="http://worldfamilyorganization.org/" target="_blank" rel="noopener noreferrer" className="group">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_pixel-perfect-132/artifacts/ch4gfwp1_WFO-Logo-Vertical%20%281%29.png" 
                    alt="World Family Organization Logo"
                    className="h-64 md:h-80 object-contain transition-transform duration-300 group-hover:scale-105"
                  />
                </a>
              </div>
              
              <div className="space-y-6">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900">
                  The World Family Organization - WFO
                </h3>
                <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                  The World Family Organization (WFO) is an International Organization which has as main objective to represent Families of the world at all levels, to defend their interests and to act with them and for them vis a vis governments and the international community.
                </p>
                <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                  With respect to the United Nations and the consultative process, the Organization shall act as the link between both the aspirations and achievements of the families and the work done for them at the level of the United Nations.
                </p>
                <a 
                  href="http://worldfamilyorganization.org/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-block mt-4 text-cyan-600 hover:text-cyan-700 font-semibold text-base md:text-lg transition-colors"
                >
                  Visit WFO Website →
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-32 bg-gradient-to-br from-cyan-500 via-blue-600 to-purple-700 text-white overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-300/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
        
        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="inline-block px-6 py-2 bg-white/20 backdrop-blur-sm rounded-full border border-white/30 mb-8">
              <p className="text-sm font-medium tracking-wide">Join Us Today</p>
            </div>
            
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-extrabold mb-6 leading-tight">
              <span style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontWeight: '400' }}>Ready to</span>{' '}
              <span className="font-black">
                <span className="bg-gradient-to-r from-white to-cyan-200 bg-clip-text text-transparent">Transform</span>
                <br />Your Future?
              </span>
            </h2>
            
            <p className="text-base md:text-xl lg:text-2xl mb-12 max-w-3xl mx-auto text-white/90 leading-relaxed">
              Join hundreds of Cubs who have already taken the first step towards success. Your journey starts here.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <Link to="/register/cub">
                <Button 
                  size="lg" 
                  className="text-base md:text-lg px-8 md:px-10 py-6 md:py-7 bg-white text-cyan-600 hover:bg-gray-100 font-bold rounded-full hover:shadow-2xl hover:scale-105 transition-all group"
                >
                  Become a Cub
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/register/lion">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="text-base md:text-lg px-8 md:px-10 py-6 md:py-7 border-2 border-white text-white hover:bg-white hover:text-cyan-600 font-bold rounded-full hover:shadow-2xl hover:scale-105 transition-all"
                >
                  Become a Lion
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;